// This is the entry point React component: QuoteManagement.jsx

import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Typography,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import toast from 'react-hot-toast';

import AddQuoteDialog from '../../components/dialog/AddQuoteDialogue';
import EditQuoteDialog from '../../components/dialog/EditQuoteDialogue';
import ConfirmReplaceDialog from '../../components/dialog/ConfirmReplaceDialogue';
import BulkUploadDialog from '../../components/dialog/BulkQuoteDialogue';
import EmptyState from '../../components/empty-state';
import {
    useQueryGetAllQuotes,
    useMutationAddQuote,
    useMutationDeleteQuote,
    useMutationUpdateQuoteByDate,
    useMutationUpdateQuoteById,
    useMutationBulkUploadQuotes,
} from '../../hooks/react-query/quotes';

// const API_URL = `${process.env.REACT_APP_SERVER_URL}/api/daily-quotes`;

const QuoteManagement = () => {
    const [quotes, setQuotes] = useState([]);
    const [form, setForm] = useState({ date: '', quote: '' });
    const [editId, setEditId] = useState(null);
    const [openAdd, setOpenAdd] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [openBulk, setOpenBulk] = useState(false);

    const { data: quotesData } = useQueryGetAllQuotes();
    const { mutateAsync: addQuote } = useMutationAddQuote(toast);
    const { mutateAsync: deleteQuote } = useMutationDeleteQuote(toast);
    const { mutateAsync: updateQuoteByDate } = useMutationUpdateQuoteByDate(toast);
    const { mutateAsync: updateQuoteById } = useMutationUpdateQuoteById(toast);
    const { mutateAsync: bulkUploadQuotes } = useMutationBulkUploadQuotes(toast);

    useEffect(() => {
        setQuotes(quotesData || []);
    }, [quotesData]);

    const handleAdd = async () => {
        try {
            const exists = quotes.find(q => q.date === form.date);
            if (exists) return setOpenConfirm(true);

            await addQuote(form);
            setOpenAdd(false);
            setForm({ date: '', quote: '' });
            return true;
        } catch (error) {
            toast.error('Error adding quote. Please try again.');
            return false;
        }
    };

    const handleUpdate = async () => {
        await updateQuoteByDate({ quote: form.quote, date: form.date });
        setOpenEdit(false);
    };

    const handleDelete = async (id) => {
        await deleteQuote(id);
    };

    const handleReplace = async () => {
        const existing = quotes.find(q => q.date === form.date);
        await updateQuoteById({ id: existing._id, quote: form.quote });
        setOpenConfirm(false);
        setOpenAdd(false);
    };

    const handleBulkUpload = async (file) => {
        try {
            await bulkUploadQuotes(file);
            setOpenBulk(false);
        } catch (error) {
            // Error toast handled inside hook via generateToastError
        }
    };

    return (
        <Box>
            <Helmet>
                <title>Edifying Teachers | Quote Management</title>
            </Helmet>

            <Container maxWidth="xl" sx={{ padding: '0 !important' }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        flexDirection: { xs: 'column', sm: 'row' },
                        rowGap: '20px',
                        mb: 2,
                    }}
                >
                    <Typography variant="h3" sx={{ mb: '5px', color: 'text.titles' }}>
                        Manage Daily Quotes
                    </Typography>
                    <Box>
                        <Button variant="contained" onClick={() => setOpenAdd(true)} sx={{ mr: 1 }}>Add Quote</Button>
                        <Button variant="outlined" onClick={() => setOpenBulk(true)}>Bulk Upload</Button>
                    </Box>
                </Box>

                {quotes?.length === 0 ? (
                    <EmptyState
                        icon={FormatQuoteIcon}
                        title="No Quotes Found"
                        description="You haven't added any daily quotes yet. Start inspiring your team by adding your first quote!"
                        iconClassName="quote-icon"
                        dotsClassName="quote-dots"
                    />

                ) : (
                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="quotes table">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Quote</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {quotes?.map((q) => (
                                    <TableRow key={q._id}>
                                        <TableCell>{q.date}</TableCell>
                                        <TableCell>{q.quote}</TableCell>
                                        <TableCell>
                                            <IconButton onClick={() => {
                                                setForm(q);
                                                setEditId(q._id);
                                                setOpenEdit(true);
                                            }}>
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton onClick={() => handleDelete(q._id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Container>

            <AddQuoteDialog
                open={openAdd}
                form={form}
                setForm={setForm}
                onClose={() => {
                    setForm({ date: '', quote: '' });
                    setOpenAdd(false)
                }}
                onSave={handleAdd}
            />

            <EditQuoteDialog
                open={openEdit}
                form={form}
                setForm={setForm}
                onClose={() => {
                    setForm({ date: '', quote: '' });
                    setOpenEdit(false)
                }}
                onSave={handleUpdate}
            />

            <ConfirmReplaceDialog
                open={openConfirm}
                date={form.date}
                onClose={() => setOpenConfirm(false)}
                onConfirm={handleReplace}
            />

            <BulkUploadDialog
                open={openBulk}
                onClose={() => setOpenBulk(false)}
                onUpload={handleBulkUpload}
            />

            {/* Toast notifications are handled via react-hot-toast */}
        </Box>
    );
};

export default QuoteManagement;
